/**
 * Knowledge 域 — API 接口
 * 
 * 对接后端 courses / knowledge podcasts / articles 知识内容
 */

import { shouldUseMock } from '../../api/config';
import { http } from '../../api/http';

// ── 类型定义 ──

export interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  coverUrl?: string;
  chaptersCount: number;
  estimatedMinutes: number;
  price?: number;
  purchased?: boolean;
}

export interface CourseDetail extends Course {
  chapters: CourseChapter[];
  author?: string;
  updatedAt?: string;
}

export interface CourseChapter {
  id: string;
  title: string;
  duration: number;
  completed?: boolean;
}

export interface Article {
  id: string;
  title: string;
  summary: string;
  category: string;
  readTime: number;
  imageUrl?: string;
  date: string;
}

export interface PodcastSeries {
  id: string;
  title: string;
  summary: string;
  topicTags: string[];
  audienceLevel: 'intro' | 'standard' | 'deep_dive';
  status: string;
  coverAssetRef?: Record<string, unknown>;
  imageRef?: Record<string, unknown>;
  shareImageRef?: Record<string, unknown>;
  imageAltText?: string;
  visualReviewStatus?: string;
  displayOrder: number;
}

export interface PodcastEpisode {
  id: string;
  seriesId: string;
  episodeNumber: number;
  title: string;
  summary: string;
  durationTargetSec: number;
  durationSec?: number | null;
  episodeBrief: Record<string, unknown>;
  transcriptRef: Record<string, unknown>;
  audioRef: Record<string, unknown>;
  evidenceRefs: unknown[];
  imageRef?: Record<string, unknown>;
  shareImageRef?: Record<string, unknown>;
  imageAltText?: string;
  visualReviewStatus?: string;
  medicalReviewStatus: string;
  status: string;
  publishedAt?: string | null;
}

export interface PodcastProgress {
  progressSec: number;
  status: 'not_started' | 'in_progress' | 'completed';
  lastPlayedAt?: string | null;
  completedAt?: string | null;
}

export interface PodcastEpisodeWithProgress extends PodcastEpisode {
  progress?: PodcastProgress | null;
}

export interface PodcastSeriesDetail {
  series: PodcastSeries;
  episodes: PodcastEpisodeWithProgress[];
}

export interface VisualCardSetSummary {
  id: string;
  status: string;
  purpose: string;
  visibility: string;
  medicalReviewStatus: string;
  imageRef?: Record<string, unknown>;
  altText?: string;
  cardCount: number;
  createdAt: string;
}

export interface VisualCardSetListResponse {
  sets: VisualCardSetSummary[];
}

export interface VisualCard {
  id: string;
  cardIndex: number;
  cardType: string;
  aspectRatio: string;
  renderPayload: Record<string, unknown>;
  textDigest: string;
  altText: string;
  imageRef: Record<string, unknown>;
  reviewStatus: string;
  createdAt: string;
}

export interface VisualCardSet {
  id: string;
  userId?: string | null;
  planId?: string | null;
  planDayId?: string | null;
  sourceType: string;
  sourceId: string;
  visibility: string;
  purpose: string;
  status: string;
  medicalReviewStatus: string;
  schemaVersion: string;
  generatorVersion: string;
  cardCount: number;
  createdAt: string;
}

export interface VisualCardSetDetail {
  set: VisualCardSet;
  cards: VisualCard[];
  privacyBoundary: string;
}

export interface KnowledgeContentChunk {
  id: string;
  sourceType: string;
  sourceId: string;
  visibility: string;
  title: string;
  chunkText: string;
  summary: string;
  topicTags: string[];
  evidenceRefs: unknown[];
  evidenceBoundary: Record<string, unknown>;
  createdAt: string;
}

export interface KnowledgeSearchResponse {
  chunks: KnowledgeContentChunk[];
}

export interface PersonalizedKnowledgeSourceRef {
  planId: string;
}

export interface PersonalizedKnowledgeQueueItem {
  id: string;
  title: string;
  summary: string;
  direction: string;
  reason: string;
  actionLabel: string;
  imageRef?: Record<string, unknown>;
  shareImageRef?: Record<string, unknown>;
  imageAltText?: string;
  visualReviewStatus?: string;
  articleTitle?: string;
  articleBody?: string;
  keyTakeaways?: string[];
  audioStatus: 'completed' | 'processing' | 'failed' | 'degraded' | string;
  readiness: 'playable' | 'audio_processing' | 'text_ready' | string;
  sourceRef: PersonalizedKnowledgeSourceRef;
}

export interface PersonalizedKnowledgeReplenishment {
  status: 'standby' | 'needs_backfill' | 'needs_plan' | string;
  trigger: string;
  nextDirection?: string | null;
}

export interface PersonalizedKnowledgeEvidenceBoundary {
  fromUserData: string[];
  missingData: string[];
  safetyNote: string;
}

export interface PersonalizedKnowledgeQueueResponse {
  planId?: string | null;
  status: 'ready' | 'empty' | 'needs_plan' | string;
  currentLessonId?: string | null;
  readyCount: number;
  items: PersonalizedKnowledgeQueueItem[];
  replenishment: PersonalizedKnowledgeReplenishment;
  evidenceBoundary: PersonalizedKnowledgeEvidenceBoundary;
}

export interface PersonalizedKnowledgeProgressRequest {
  planId: string;
  lessonId: string;
}

export interface PersonalizedKnowledgeHistoryItem extends PersonalizedKnowledgeQueueItem {
  completedAt?: string | null;
}

export interface PersonalizedKnowledgeHistoryResponse {
  planId?: string | null;
  items: PersonalizedKnowledgeHistoryItem[];
}

function mockVisualRef(assetKey: string, palette: string, imageKind = 'thumbnail_image') {
  return {
    imageKind,
    assetKey,
    palette,
    privacy: assetKey.includes('personalized') ? 'private_user' : 'public',
    reviewStatus: 'not_required',
  };
}

function mockPersonalizedKnowledgeQueue(): PersonalizedKnowledgeQueueResponse {
  return {
    planId: 'mock-longevity-plan',
    status: 'ready',
    currentLessonId: 'mock-longevity-day-1',
    readyCount: 3,
    items: [
      {
        id: 'mock-longevity-day-1',
        title: '把“心肺底盘”拆成可以开始的一件事',
        summary: '先把长期目标翻译成低摩擦行动，再决定要不要加量。',
        direction: '心肺底盘',
        reason: '基于“心肺底盘”和“低强度步行”整理。',
        actionLabel: '低强度步行 10 分钟',
        imageRef: mockVisualRef('knowledge/personalized/A/thumbnail_image', 'personal-action'),
        shareImageRef: mockVisualRef('knowledge/personalized/A/share_card', 'personal-action', 'share_card'),
        imageAltText: '心肺底盘个性化真知课程图片',
        visualReviewStatus: 'not_required',
        articleTitle: '把“心肺底盘”拆成可以开始的一件事',
        articleBody:
          '### 这一节讲什么\n先把长期目标翻译成低摩擦行动，再决定要不要加量。\n\n### 今天怎么用\n先落到低强度步行 10 分钟，完成后观察身体反馈。',
        keyTakeaways: ['从小剂量开始', '完成后看身体反馈', '不舒服先暂停'],
        audioStatus: 'degraded',
        readiness: 'text_ready',
        sourceRef: { planId: 'mock-longevity-plan' },
      },
      {
        id: 'mock-longevity-day-2',
        title: '从力量储备看懂为什么动作要小一点',
        summary: '先稳定完成，再让身体给出反馈。',
        direction: '力量储备',
        reason: '基于“力量储备”和“坐站练习”整理。',
        actionLabel: '坐站练习 6 次',
        imageRef: mockVisualRef('knowledge/personalized/B/thumbnail_image', 'personal-recovery'),
        shareImageRef: mockVisualRef('knowledge/personalized/B/share_card', 'personal-recovery', 'share_card'),
        imageAltText: '力量储备个性化真知课程图片',
        visualReviewStatus: 'not_required',
        articleTitle: '从力量储备看懂为什么动作要小一点',
        articleBody:
          '### 这一节讲什么\n先稳定完成，再让身体给出反馈。\n\n### 今天怎么用\n把理解落到坐站练习 6 次，状态一般时先缩小剂量。',
        keyTakeaways: ['先稳定完成', '动作质量优先', '状态一般就降级'],
        audioStatus: 'degraded',
        readiness: 'text_ready',
        sourceRef: { planId: 'mock-longevity-plan' },
      },
      {
        id: 'mock-longevity-day-3',
        title: '先划清安全边界：什么情况要降级或暂停',
        summary: '把不适、恢复不足和缺失信息转成清晰边界，避免硬扛。',
        direction: '安全边界',
        reason: '基于安全停止线、缺失信息和可替代行动整理。',
        actionLabel: '轻量活动 5 分钟',
        imageRef: mockVisualRef('knowledge/personalized/C/thumbnail_image', 'personal-safety'),
        shareImageRef: mockVisualRef('knowledge/personalized/C/share_card', 'personal-safety', 'share_card'),
        imageAltText: '安全边界个性化真知课程图片',
        visualReviewStatus: 'not_required',
        articleTitle: '先划清安全边界：什么情况要降级或暂停',
        articleBody:
          '### 这一节讲什么\n把不适、恢复不足和缺失信息转成清晰边界。\n\n### 今天怎么用\n做轻量活动 5 分钟，并把异常反馈记录下来。',
        keyTakeaways: ['先守住边界', '不适先停', '缺数据时不硬加量'],
        audioStatus: 'degraded',
        readiness: 'text_ready',
        sourceRef: { planId: 'mock-longevity-plan' },
      },
    ],
    replenishment: {
      status: 'standby',
      trigger: '选择某一节后，后续内容会顺着这个方向延展。',
      nextDirection: '力量储备',
    },
    evidenceBoundary: {
      fromUserData: ['百岁目标', '实践反馈'],
      missingData: ['连续可穿戴数据'],
      safetyNote: '真知只解释健康知识与行动理由，不替代医学诊断。',
    },
  };
}

function mockCompletedPersonalizedKnowledgeQueue(
  data: PersonalizedKnowledgeProgressRequest,
): PersonalizedKnowledgeQueueResponse {
  const queue = mockPersonalizedKnowledgeQueue();
  if (data.lessonId !== 'mock-longevity-day-2') return queue;
  return {
    ...queue,
    currentLessonId: 'mock-longevity-day-1',
    items: [
      queue.items[0],
      {
        id: 'mock-longevity-day-5',
        title: '沿着身体余量再深入：什么时候可以多一点',
        summary: '在身体反馈稳定时，再判断能不能稍微推进一点。',
        direction: '身体余量',
        reason: '基于近期健康线索、恢复状态和当前行动难度整理。',
        actionLabel: '坐站练习 8 次',
        imageRef: mockVisualRef('knowledge/personalized/B2/thumbnail_image', 'personal-recovery'),
        shareImageRef: mockVisualRef('knowledge/personalized/B2/share_card', 'personal-recovery', 'share_card'),
        imageAltText: '身体余量个性化真知课程图片',
        visualReviewStatus: 'not_required',
        audioStatus: 'degraded',
        readiness: 'text_ready',
        sourceRef: { planId: data.planId },
      },
      queue.items[2],
    ],
  };
}

function mockPersonalizedKnowledgeHistory(): PersonalizedKnowledgeHistoryResponse {
  const queue = mockPersonalizedKnowledgeQueue();
  return {
    planId: 'mock-longevity-plan',
    items: [
      {
        ...queue.items[1],
        completedAt: new Date().toISOString(),
      },
    ],
  };
}

function mockPodcastSeries(): PodcastSeries[] {
  return [
    {
      id: 'mock-foundation-cardio',
      title: '心肺能力为什么是长寿底盘',
      summary: '用通俗语言理解 Zone 2、日常活动和心肺余量的关系，帮助你判断从哪里开始。',
      topicTags: ['心肺', '运动', '长寿'],
      audienceLevel: 'intro',
      status: 'published',
      coverAssetRef: mockVisualRef('knowledge/mock-foundation-cardio/series-cover', 'cardio'),
      imageRef: mockVisualRef('knowledge/mock-foundation-cardio/series-cover', 'cardio'),
      shareImageRef: mockVisualRef('knowledge/mock-foundation-cardio/series-share-card', 'cardio', 'share_card'),
      imageAltText: '心肺能力课程封面',
      visualReviewStatus: 'passed',
      displayOrder: 1,
    },
    {
      id: 'mock-strength-stability',
      title: '力量和稳定性如何保护日常行动',
      summary: '把肌肉、平衡和关节安全放进同一张地图，避免把训练误解成硬扛。',
      topicTags: ['力量', '稳定性', '安全边界'],
      audienceLevel: 'standard',
      status: 'published',
      coverAssetRef: mockVisualRef('knowledge/mock-strength-stability/series-cover', 'movement'),
      imageRef: mockVisualRef('knowledge/mock-strength-stability/series-cover', 'movement'),
      shareImageRef: mockVisualRef('knowledge/mock-strength-stability/series-share-card', 'movement', 'share_card'),
      imageAltText: '力量和稳定性课程封面',
      visualReviewStatus: 'passed',
      displayOrder: 2,
    },
    {
      id: 'mock-clinical-literacy',
      title: '看懂健康研究时先问哪三个问题',
      summary: '从相对风险、绝对风险和适用人群开始，减少被单条结论带偏。',
      topicTags: ['循证', '风险', '判断力'],
      audienceLevel: 'standard',
      status: 'published',
      coverAssetRef: mockVisualRef('knowledge/mock-clinical-literacy/series-cover', 'evidence'),
      imageRef: mockVisualRef('knowledge/mock-clinical-literacy/series-cover', 'evidence'),
      shareImageRef: mockVisualRef('knowledge/mock-clinical-literacy/series-share-card', 'evidence', 'share_card'),
      imageAltText: '医学证据课程封面',
      visualReviewStatus: 'passed',
      displayOrder: 3,
    },
  ];
}

function mockPodcastEpisode(series: PodcastSeries, episodeNumber = 1): PodcastEpisodeWithProgress {
  return {
    id: `${series.id}-episode-${episodeNumber}`,
    seriesId: series.id,
    episodeNumber,
    title: episodeNumber === 1 ? series.title : `${series.title}：实践前的边界`,
    summary: series.summary,
    durationTargetSec: 12 * 60,
    durationSec: null,
    episodeBrief: {
      thesis: series.summary,
      takeaways: [
        '先理解一般规律，再判断它是否适合自己的当前状态。',
        '缺少个人资料时，只能作为背景知识，不能直接当成建议。',
        '不舒服、症状加重或涉及用药时，应向医生确认。',
      ],
    },
    transcriptRef: {},
    audioRef: {},
    evidenceRefs: [],
    imageRef: mockVisualRef(`knowledge/${series.id}/episode-${episodeNumber}-thumbnail`, 'evidence'),
    shareImageRef: mockVisualRef(`knowledge/${series.id}/episode-${episodeNumber}-share-card`, 'evidence', 'share_card'),
    imageAltText: `${series.title}课程图片`,
    visualReviewStatus: 'passed',
    medicalReviewStatus: 'reviewed',
    status: 'published',
    publishedAt: new Date().toISOString(),
    progress: null,
  };
}

// ── API 调用 ──

export const knowledgeApi = {
  /** 课程列表 */
  async getCourses(): Promise<Course[]> {
    const res = await http.get<{ courses: Course[] }>('/courses');
    return res.courses ?? [];
  },

  /** 课程详情 */
  async getCourseDetail(id: string): Promise<CourseDetail> {
    return http.get<CourseDetail>(`/courses/${id}`);
  },

  /** 我的课程 */
  async getMyCourses(): Promise<Course[]> {
    const res = await http.get<{ courses: Course[] }>('/user/courses');
    return res.courses ?? [];
  },

  /** 文章搜索 */
  async searchArticles(q: string, limit = 10): Promise<Article[]> {
    const res = await http.get<{ articles: Article[] }>(`/articles/search?q=${encodeURIComponent(q)}&limit=${limit}`);
    return res.articles ?? [];
  },

  /** 文章详情 */
  async getArticle(id: string): Promise<Article> {
    return http.get<Article>(`/articles/${id}`);
  },

  /** 通识播客系列 */
  async getPodcastSeries(): Promise<PodcastSeries[]> {
    if (shouldUseMock('knowledge') || shouldUseMock('auth')) {
      return mockPodcastSeries();
    }
    return http.get<PodcastSeries[]>('/knowledge/podcasts/series', {
      skipAuth: shouldUseMock('auth'),
    });
  },

  /** 通识播客系列详情 */
  async getPodcastSeriesDetail(id: string): Promise<PodcastSeriesDetail> {
    if (shouldUseMock('knowledge') || shouldUseMock('auth')) {
      const series =
        mockPodcastSeries().find((item) => item.id === id) ??
        mockPodcastSeries()[0];
      return {
        series,
        episodes: [mockPodcastEpisode(series, 1), mockPodcastEpisode(series, 2)],
      };
    }
    return http.get<PodcastSeriesDetail>(`/knowledge/podcasts/series/${id}`, {
      skipAuth: shouldUseMock('auth'),
    });
  },

  /** 通识播客单集详情 */
  async getPodcastEpisode(id: string): Promise<PodcastEpisodeWithProgress> {
    if (shouldUseMock('knowledge') || shouldUseMock('auth')) {
      const series =
        mockPodcastSeries().find((item) => id.startsWith(item.id)) ??
        mockPodcastSeries()[0];
      const episodeNumber = id.endsWith('episode-2') ? 2 : 1;
      return mockPodcastEpisode(series, episodeNumber);
    }
    return http.get<PodcastEpisodeWithProgress>(`/knowledge/podcasts/episodes/${id}`, {
      skipAuth: shouldUseMock('auth'),
    });
  },

  /** 写入通识播客进度 */
  async updatePodcastProgress(id: string, data: { progressSec: number; status?: PodcastProgress['status'] }) {
    if (shouldUseMock('knowledge')) {
      const now = new Date().toISOString();
      return {
        progressSec: data.progressSec,
        status: data.status ?? 'in_progress',
        lastPlayedAt: now,
        completedAt: data.status === 'completed' ? now : null,
      } satisfies PodcastProgress;
    }
    return http.post<PodcastProgress>(`/knowledge/podcasts/episodes/${id}/progress`, data);
  },

  /** 通识播客单集信息卡组 */
  async getPodcastEpisodeVisualCardSets(id: string): Promise<VisualCardSetListResponse> {
    if (shouldUseMock('knowledge') || shouldUseMock('auth')) {
      return { sets: [] };
    }
    return http.get<VisualCardSetListResponse>(`/knowledge/podcasts/episodes/${id}/visual-card-sets`, {
      skipAuth: shouldUseMock('auth'),
    });
  },

  /** 个性化真知候选队列 */
  async getPersonalizedQueue(): Promise<PersonalizedKnowledgeQueueResponse> {
    if (shouldUseMock('knowledge') || shouldUseMock('auth')) {
      return mockPersonalizedKnowledgeQueue();
    }
    return http.get<PersonalizedKnowledgeQueueResponse>('/knowledge/personalized/queue');
  },

  /** 选择个性化真知方向 */
  async selectPersonalizedQueueItem(
    data: PersonalizedKnowledgeProgressRequest,
  ): Promise<PersonalizedKnowledgeQueueResponse> {
    if (shouldUseMock('knowledge') || shouldUseMock('auth')) {
      return {
        ...mockPersonalizedKnowledgeQueue(),
        currentLessonId: data.lessonId,
      };
    }
    return http.post<PersonalizedKnowledgeQueueResponse>(
      '/knowledge/personalized/queue/select',
      data,
    );
  },

  /** 完成个性化真知收听 */
  async completePersonalizedQueueItem(
    data: PersonalizedKnowledgeProgressRequest,
  ): Promise<PersonalizedKnowledgeQueueResponse> {
    if (shouldUseMock('knowledge') || shouldUseMock('auth')) {
      return mockCompletedPersonalizedKnowledgeQueue(data);
    }
    return http.post<PersonalizedKnowledgeQueueResponse>(
      '/knowledge/personalized/queue/complete',
      data,
    );
  },

  /** 个性化真知历史 */
  async getPersonalizedHistory(): Promise<PersonalizedKnowledgeHistoryResponse> {
    if (shouldUseMock('knowledge') || shouldUseMock('auth')) {
      return mockPersonalizedKnowledgeHistory();
    }
    return http.get<PersonalizedKnowledgeHistoryResponse>('/knowledge/personalized/history');
  },

  /** 图片信息卡组详情 */
  async getVisualCardSet(id: string): Promise<VisualCardSetDetail> {
    return http.get<VisualCardSetDetail>(`/visual-card-sets/${id}`, {
      skipAuth: shouldUseMock('auth'),
    });
  },

  /** 解惑可引用知识切片 */
  async searchKnowledge(params: {
    q?: string;
    sourceType?: string;
    sourceId?: string;
    episodeId?: string;
    planId?: string;
    limit?: number;
  }): Promise<KnowledgeSearchResponse> {
    if (shouldUseMock('knowledge') || shouldUseMock('auth')) {
      return { chunks: [] };
    }
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
    });
    return http.get<KnowledgeSearchResponse>(`/knowledge/search?${query.toString()}`);
  },
};
